import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fromJS } from "immutable";
import { format, isWeekend, parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { FileImage, FileVideo, Trash2, ThumbsUp, Eye, MessageCircle, Share2, Repeat2, Heart, RefreshCw } from "lucide-react";
import { Modal } from "../../components/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { getActivityFileMetrics, deleteTask, deleteFileLogActivity } from "../../api/ProjectApi";
import { formatBytes } from "../helpers";

import { getPlatformIcon, getPlatformStyle } from "../constants/platformIcons";
import ImageView from "./ImageView";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import { useDispatch, useSelector } from "react-redux";
import AIContentAction from "../../actions/AIContentAction";
import LayoutAction from "../../actions/LayoutAction";
import RuleAction from "../../actions/RuleAction";
import AppSocial from "../../rules/components/AppSocial";

const STATUS_COLORS: Record<string, string> = {
  PUBLISHED: "bg-green-100 text-green-700",
  SCHEDULED: "bg-blue-100 text-blue-700",
  DRAFT: "bg-gray-100 text-gray-600",
  FAILED: "bg-red-100 text-red-700",
  PUBLISHING: "bg-yellow-100 text-yellow-700",
  CANCELLED: "bg-gray-100 text-gray-500",
  RETRYING: "bg-orange-100 text-orange-700",
};

const getMediaExt = (name: string) => (name.split(".").pop() || "").toLowerCase();

export interface MediaDetailItem {
  id: string;
  fileName: string;
  createdAt?: string | number;
  isVideo?: boolean;
  fileSize?: number;
  width?: number;
  height?: number;
  source?: string;
  mediaInsights?: {
    postCount?: number;
    publishedCount?: number;
    platforms?: string[];
    totalLikeCount?: number;
    totalCommentCount?: number;
    totalImpressionsCount?: number;
    totalReachCount?: number;
    totalShareCount?: number;
    totalSavedCount?: number;
    totalViewCount?: number;
    totalVideoViewsCount?: number;
    totalQuotesCount?: number;
    totalRepostsCount?: number;
  };
  fileContentType?: string;
  visibility?: string;
}

interface MediaDetailModalProps {
  projectId: string;
  item: MediaDetailItem | null;
  onClose: () => void;
  onDelete?: (id: string) => void;
  /** Called when fresh metrics differ from the cached item.mediaInsights so the list can be reloaded */
  onInsightsChanged?: () => void;
}

const MediaDetailModal: React.FC<MediaDetailModalProps> = ({ projectId, item, onClose, onDelete, onInsightsChanged }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));
  const aiSetting =
    useSelector((state: any) => state.projectReducer.getIn(["project", "aiSetting"])) || fromJS({});

  const [confirmDeletePostId, setConfirmDeletePostId] = useState<string | null>(null);

  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => deleteTask(postId),
    onSuccess: () => {
      setConfirmDeletePostId(null);
      queryClient.invalidateQueries({ queryKey: ["media-metrics", id] });
    },
  });

  const [showDeleteMediaConfirm, setShowDeleteMediaConfirm] = useState(false);
  const deleteMediaMutation = useMutation({
    mutationFn: () => deleteFileLogActivity(id),
    onSuccess: () => {
      setShowDeleteMediaConfirm(false);
      onDelete?.(id);
      onClose();
    },
    onError: (error: any) => {
      setShowDeleteMediaConfirm(false);
      const status = error?.response?.status;
      const message = status === 409
        ? t("Cannot delete this media because it is used in one or more posts.")
        : t("Failed to delete media: {{error}}", { error: String(error?.message || error) });
      dispatch(LayoutAction.setNotification({ title: t("Delete Media"), type: "error", message }) as any);
    },
  });

  const isOpen = !!item;
  const id = item?.id ?? "";
  const fileName = item?.fileName ?? "";
  const createdAt = item?.createdAt;
  const isVideo = item?.isVideo ?? false;
  const shortName = fileName.split("/").pop() || fileName;
  const ext = getMediaExt(fileName);

  const { data: metrics, isLoading: isMetricsLoading, isFetching, refetch } = useQuery({
    queryKey: ["media-metrics", id],
    queryFn: () => getActivityFileMetrics(id),
    enabled: !!id,
  });

  React.useEffect(() => {
    dispatch(RuleAction.getAppLinks());
  }, [dispatch]);

  // After a successful refetch, compare live aggregated totals against cached mediaInsights.
  // If any metric differs, notify the parent to reload the media list.
  const prevFetchingRef = React.useRef(false);
  React.useEffect(() => {
    const wasJustFetched = prevFetchingRef.current && !isFetching;
    prevFetchingRef.current = isFetching;
    if (!wasJustFetched || !metrics || !onInsightsChanged) return;

    const ins = item?.mediaInsights;
    const posts: any[] = metrics?.posts ?? [];

    // Aggregate live totals from engagement.metrics.data (success only)
    const liveTotals: Record<string, number> = {};
    for (const post of posts) {
      const eng = post?.engagement ?? {};
      if (!eng.success || !eng.metrics) continue;
      for (const m of (eng.metrics?.data ?? [])) {
        const name: string = (m?.name ?? "").toLowerCase();
        liveTotals[name] = (liveTotals[name] ?? 0) + (m?.values?.[0]?.value ?? 0);
      }
    }

    const liveViews = liveTotals["views"] ?? 0;
    const liveLikes = liveTotals["likes"] ?? 0;
    const liveReposts = liveTotals["reposts"] ?? 0;
    const liveQuotes = liveTotals["quotes"] ?? 0;
    const liveReplies = liveTotals["replies"] ?? 0;
    const livePostCount = posts.length;
    const livePublished = posts.filter((p: any) => p.status === "PUBLISHED").length;

    const changed =
      livePostCount !== (ins?.postCount ?? 0) ||
      livePublished !== (ins?.publishedCount ?? 0) ||
      liveViews !== (ins?.totalViewCount ?? 0) ||
      liveLikes !== (ins?.totalLikeCount ?? 0) ||
      liveReposts !== (ins?.totalRepostsCount ?? 0) ||
      liveQuotes !== (ins?.totalQuotesCount ?? 0) ||
      liveReplies !== (ins?.totalCommentCount ?? 0);

    if (changed) {
      onInsightsChanged();
    }
  }, [isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  const formattedDate = createdAt
    ? (() => {
      // createdAt can be a Unix timestamp (seconds) or an ISO string
      const raw = typeof createdAt === "number"
        ? new Date(createdAt < 2e10 ? createdAt * 1000 : createdAt)
        : new Date(typeof createdAt === "string" && /^\d+(\.\d+)?$/.test(createdAt)
          ? parseFloat(createdAt) * 1000
          : createdAt);
      return raw.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    })()
    : "—";

  const socialApps = ["facebook", "instagram", "twitter", "linkedin", "threads"];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title=""
        showHeader={true}
        showCloseButton={true}
        closeOnBackdropClick
        className="w-[900px] max-w-[95vw] max-h-[90vh] overflow-hidden rounded-2xl !p-0"
        rightExtend={
          <div className="flex items-center gap-1">
            <Menu as="div" className="pr-10 relative inline-block shrink-0">
              <MenuButton className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer outline-none">
                <Share2 className="w-3 h-3" />
                <span>{t("Share")}</span>
              </MenuButton>
              <MenuItems className="absolute left-0 z-20 py-2 origin-top-left bg-white shadow-xl rounded ring-opacity-5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-leave:duration-75 data-enter:ease-out data-leave:ease-in">
                {socialApps.map((socialApp) => {
                  const app = appLinks.toJS().find((item: any) => item.app === socialApp);
                  const isConnected = app?.connected || false;
                  if (!isConnected) return null;
                  return (
                    <MenuItem key={socialApp} >
                      <button
                        className="w-full text-left px-2 py-2 hover:bg-gray-100"
                        onClick={() => {
                          onClose();
                          const timezone = aiSetting.get("timezone");
                          const startTime = aiSetting.get("startTime") || "09:00";
                          const startDate = format(new Date(), "yyyy-MM-dd");
                          const localDate = parse(
                            `${startDate} ${startTime}`,
                            'yyyy-MM-dd HH:mm',
                            new Date()
                          );
                          const startDateTime = zonedTimeToUtc(localDate, timezone);
                          dispatch(AIContentAction.showSharePostDialog({
                            projectId,
                            type: "SOCIAL_POST",
                            data: {
                              summary: { "value": "", "label": "summary", "type": "string" },
                              scheduledTime: { "value": startDateTime.getTime(), "label": "scheduledTime", "type": "datetime" },
                              postType: { "value": "image", "label": "postType", "type": "string" },
                              timezone: { "value": aiSetting.get("timezone"), "label": "timezone", "type": "string" },
                              weekend: { "value": isWeekend(startDateTime), "label": "weekend", "type": "bool" },
                              start: { "value": startDateTime.getTime(), "label": "start", "type": "datetime" },
                              description: { "value": "", "label": "description", "type": "HTML" },
                              assignees: { "value": [currentUser.get("accountId")], "label": "assignees", "type": "stringArray" },
                              language: { "value": aiSetting.get("language"), "label": "language", "type": "string" },
                              mediaType: { "value": "HOST", "label": "mediaType", "type": "string" },
                              mediaId: { "value": fileName, "label": "mediaId", "type": "string" },
                              mediaIds: { "value": [fileName], "label": "mediaIds", "type": "stringArray" },
                              platform: { value: socialApp, label: "platform", type: "string" },
                              labels: { "value": ["ai-generated", socialApp], "label": "labels", "type": "stringArray" },
                              contentVariation: { "value": aiSetting.get("contentVariation"), "label": "contentVariation", "type": "string" },
                              dayOfWeek: { "value": format(startDateTime, 'EEEE'), "label": "dayOfWeek", "type": "string" },
                              organizer: { "value": currentUser.get("accountId"), "label": "organizer", "type": "string" },
                              topic: { "value": aiSetting.get("topic"), "label": "topic", "type": "string" },
                              status: { "value": "DRAFT", "label": "status", "type": "string" },
                            },
                          }));
                        }}
                      >
                        <AppSocial
                          appId={socialApp}
                          connected={isConnected}
                          onConnect={() => {
                            navigate(`/workspaces/${projectId}/settings/apps?key=Apps&appId=${app.app}`);
                          }}
                        />
                      </button>
                    </MenuItem>
                  );
                })}
              </MenuItems>
            </Menu>
            <button
              className="w-7 h-7 flex items-center justify-center hover:bg-blue-50 rounded-lg transition-colors text-gray-400 hover:text-blue-500"
              title={t("Refresh")}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin text-blue-500" : ""}`} />
            </button>
            <button
              className="w-7 h-7 flex items-center justify-center hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500"
              title={t("Delete media")}
              onClick={() => setShowDeleteMediaConfirm(true)}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        }
      >
        {/* Full 2-column layout — left panel fixed, right panel scrolls */}
        <div className="flex flex-row h-[80vh] max-h-[700px] overflow-hidden">
          {/* ── Left: Preview (fixed, no scroll) ── */}
          <div className="flex-1 bg-gray-50 flex items-center justify-center relative overflow-hidden min-h-0">
            {isVideo ? (
              <div className="flex flex-col items-center gap-3">
                <FileVideo className="w-16 h-16 text-gray-500" />
                <span className="text-gray-400 text-sm uppercase tracking-widest">{ext}</span>
              </div>
            ) : (
              <ImageView
                fileName={fileName}
                className="w-full h-full"
                imgClassName="object-contain"
              />
            )}

          </div>

          {/* ── Right: Metadata panel (scrollable) ── */}
          <div className="w-72 shrink-0 flex flex-col border-l border-gray-100 bg-white overflow-y-auto min-h-0">
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-start gap-2">
                {isVideo
                  ? <FileVideo className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                  : <FileImage className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                }
                <p className="text-sm font-semibold text-gray-900 break-all leading-snug">{shortName}</p>
              </div>
              <span className="mt-2 inline-flex items-center text-[11px] font-medium uppercase tracking-wide bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                {ext || "file"}
              </span>
            </div>

            {/* Insights = mediaInsights */}
            <div className="px-5 py-4 space-y-4 flex-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t("Insights")}</p>

              {/* Insights stats grid */}
              {isMetricsLoading ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="animate-pulse">
                      <div className="h-2.5 bg-gray-100 rounded w-2/3 mb-1.5" />
                      <div className="h-6 bg-gray-200 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : (() => {
                const posts: any[] = metrics?.posts ?? [];

                // Aggregate metrics from all posts
                // API structure: post.engagement = { success, metrics: { data: [{name, values:[{value}]}] } | null }
                // Only read when engagement.success === true and metrics is not null
                const totals: Record<string, number> = {};
                for (const post of posts) {
                  const eng = post?.engagement ?? {};
                  if (!eng.success || !eng.metrics) continue;

                  const engData: any[] = eng.metrics?.data ?? [];
                  for (const metric of engData) {
                    const name: string = (metric?.name ?? "").toLowerCase();
                    const val: number = metric?.values?.[0]?.value ?? 0;
                    totals[name] = (totals[name] ?? 0) + val;
                  }
                }

                // Map raw metric name → display label (covers Threads/Meta/LinkedIn/Twitter naming)
                const METRIC_LABEL_MAP: Record<string, string> = {
                  likes: t("Likes"), like: t("Likes"), reactions: t("Likes"),
                  comments: t("Comments"), replies: t("Comments"),
                  impressions: t("Impressions"),
                  reach: t("Reach"),
                  shares: t("Shares"),
                  reposts: t("Reposts"), retweets: t("Reposts"),
                  quotes: t("Quotes"), quote_tweets: t("Quotes"),
                  saved: t("Saves"), saves: t("Saves"), bookmarks: t("Saves"),
                  views: t("Views"),
                  video_views: t("Video Views"), plays: t("Video Views"),
                };

                // Group raw totals into display labels (live API)
                const labelTotals: Record<string, number> = {};
                for (const [name, val] of Object.entries(totals)) {
                  const label = METRIC_LABEL_MAP[name.toLowerCase()];
                  if (label) {
                    labelTotals[label] = (labelTotals[label] ?? 0) + val;
                  }
                }

                // Merge with item.mediaInsights (pre-aggregated server totals)
                // Use Math.max to take the highest value from either source
                const ins = item?.mediaInsights;
                const merge = (label: string, insVal?: number | null) => {
                  if (insVal == null) return;
                  labelTotals[label] = Math.max(labelTotals[label] ?? 0, insVal);
                };
                merge(t("Likes"), ins?.totalLikeCount);
                merge(t("Comments"), ins?.totalCommentCount);
                merge(t("Impressions"), ins?.totalImpressionsCount);
                merge(t("Reach"), ins?.totalReachCount);
                merge(t("Shares"), ins?.totalShareCount);
                merge(t("Saves"), ins?.totalSavedCount);
                merge(t("Views"), ins?.totalViewCount);
                merge(t("Video Views"), ins?.totalVideoViewsCount);
                merge(t("Reposts"), ins?.totalRepostsCount);
                merge(t("Quotes"), ins?.totalQuotesCount);

                // postCount / publishedCount: take max from both sources
                const postCount = Math.max(posts.length, ins?.postCount ?? 0);
                const publishedCount = Math.max(
                  posts.filter((p: any) => p.status === "PUBLISHED").length,
                  ins?.publishedCount ?? 0
                );

                // Ordered display
                const ORDER = [
                  t("Likes"), t("Comments"), t("Impressions"), t("Reach"),
                  t("Shares"), t("Saves"), t("Views"), t("Video Views"),
                  t("Reposts"), t("Quotes"),
                ];

                const engagementStats = [
                  { label: t("Posts"), value: postCount },
                  { label: t("Published"), value: publishedCount },
                  ...ORDER
                    .filter(label => labelTotals[label] != null)
                    .map(label => ({ label, value: labelTotals[label] })),
                ];

                // Platforms: union of live posts and mediaInsights.platforms as fallback
                const livePlatforms = posts.map((p: any) => p.platform).filter(Boolean);
                const platforms = Array.from(new Set([
                  ...livePlatforms,
                  ...(ins?.platforms ?? []),
                ])) as string[];

                return (
                  <div className="space-y-4">
                    {/* 2-column stat grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                      {engagementStats.map((stat) => (
                        <div key={stat.label}>
                          <p className="text-xs text-gray-400 mb-0.5">{stat.label}</p>
                          <p className="text-xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>

                    {/* Platforms stacked */}
                    {platforms.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 mb-1.5">{t("Platforms")}</p>
                        <div
                          className="flex items-center flex-wrap gap-y-1"
                          title={platforms.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(", ")}
                        >
                          {platforms.slice(0, 6).map((p: string, i: number) => {
                            const PIcon = getPlatformIcon(p);
                            const style = getPlatformStyle(p);
                            return (
                              <span
                                key={p}
                                className="w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-white flex-shrink-0"
                                style={{ background: style.color, marginLeft: i === 0 ? 0 : "-6px", zIndex: platforms.length - i }}
                              >
                                <PIcon className="w-3 h-3 text-white" />
                              </span>
                            );
                          })}
                          {platforms.length > 6 && (
                            <span
                              className="w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-white bg-gray-200 text-[9px] font-bold text-gray-500"
                              style={{ marginLeft: "-6px" }}
                            >
                              +{platforms.length - 6}
                            </span>
                          )}
                          <span className="ml-2 text-xs text-gray-500">
                            {platforms.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(", ")}
                          </span>
                        </div>
                      </div>
                    )}

                    {postCount === 0 && (
                      <p className="text-xs text-gray-400">{t("No posts yet")}</p>
                    )}
                  </div>
                );
              })()}

              {/* File info */}
              <div className="pt-3 border-t border-gray-100 flex flex-col gap-2.5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t("File Info")}</p>

                {formattedDate && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">{t("Uploaded")}</p>
                    <p className="text-xs font-medium text-gray-700">{formattedDate}</p>
                  </div>
                )}
                {item?.fileContentType && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">{t("Type")}</p>
                    <p className="text-xs font-medium text-gray-700">{item.fileContentType}</p>
                  </div>
                )}
                {item?.fileSize && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">{t("File size")}</p>
                    <p className="text-xs font-medium text-gray-700">{formatBytes(item.fileSize)}</p>
                  </div>
                )}
                {item?.width && item?.height && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">{t("Dimensions")}</p>
                    <p className="text-xs font-medium text-gray-700">{item.width} × {item.height} px</p>
                  </div>
                )}
                {item?.visibility && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">{t("Visibility")}</p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.visibility === "Shared"
                      ? "bg-blue-50 text-blue-600"
                      : "bg-gray-100 text-gray-600"
                      }`}>
                      {t(item.visibility)}
                    </span>
                  </div>
                )}
                {item?.source && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">{t("Source")}</p>
                    <p className="text-xs font-medium text-gray-700">{item.source}</p>
                  </div>
                )}
              </div>

              {/* Usage */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t("Posts")}</p>
                  {!isMetricsLoading && (
                    <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                      {metrics?.posts?.length || 0}
                    </span>
                  )}
                </div>

                {isMetricsLoading ? (
                  <div className="space-y-2">
                    {[1, 2].map(i => (
                      <div key={i} className="animate-pulse flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                        <div className="w-7 h-7 rounded-md bg-gray-200 shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-2.5 bg-gray-200 rounded w-1/3" />
                          <div className="h-2 bg-gray-100 rounded w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : metrics?.posts?.length ? (
                  <div className="space-y-2">
                    {metrics.posts.map((post: any) => {
                      const platformKey = (post.platform || "").toLowerCase();
                      const PlatformIcon = getPlatformIcon(platformKey);
                      const platformStyle = getPlatformStyle(platformKey);
                      const statusColor = STATUS_COLORS[post.status] || "bg-gray-100 text-gray-600";
                      const eng = post.engagement || {};
                      return (
                        <div
                          key={post.postId}
                          className="group flex flex-col gap-1.5 p-2.5 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors cursor-default"
                        >
                          {/* Row 1: platform + status */}
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                              style={{ background: platformStyle.color }}
                            >
                              <PlatformIcon className="w-4 h-4 text-white" />
                            </div>
                            <button
                              className="text-xs font-semibold text-gray-700 capitalize flex-1 truncate text-left hover:text-blue-600 hover:underline transition-colors"
                              title={t("View post")}
                              onClick={(e) => { e.stopPropagation(); dispatch(AIContentAction.showPostDetailDialog(post.postId) as any); }}
                            >
                              {post.platform || "—"}
                            </button>
                            {post.status && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusColor}`}>
                                {post.status}
                              </span>
                            )}
                          </div>

                          {/* Row 2: content preview */}
                          {post.contentPreview && (
                            <p className="text-[11px] text-gray-500 leading-snug line-clamp-2 pl-8">
                              {post.contentPreview}
                            </p>
                          )}

                          {/* Row 3: engagement */}
                          {(eng.likeCount != null || eng.viewCount != null || eng.commentCount != null) && (
                            <div className="flex items-center gap-3 pl-8 pt-0.5">
                              {eng.likeCount != null && (
                                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                                  <ThumbsUp className="w-3 h-3" /> {eng.likeCount}
                                </span>
                              )}
                              {eng.viewCount != null && (
                                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                                  <Eye className="w-3 h-3" /> {eng.viewCount}
                                </span>
                              )}
                              {eng.commentCount != null && (
                                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                                  <MessageCircle className="w-3 h-3" /> {eng.commentCount}
                                </span>
                              )}
                              {eng.shareCount != null && (
                                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                                  <Share2 className="w-3 h-3" /> {eng.shareCount}
                                </span>
                              )}
                              {eng.repostCount != null && (
                                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                                  <Repeat2 className="w-3 h-3" /> {eng.repostCount}
                                </span>
                              )}
                              {eng.reactionsCount != null && (
                                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                                  <Heart className="w-3 h-3" /> {eng.reactionsCount}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Row 4: delete button — DRAFT only */}
                          {post.status === "DRAFT" && (
                            <div className="flex justify-end pt-0.5">
                              <button
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all"
                                title={t("Delete post")}
                                onClick={(e) => { e.stopPropagation(); setConfirmDeletePostId(post.postId); }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-4 gap-2 text-gray-400">
                    <FileImage className="w-8 h-8 opacity-30" />
                    <p className="text-xs">{t("Not used in any post yet")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        visible={!!confirmDeletePostId}
        title={t("Delete Post")}
        description={t("This post will be permanently deleted. This action cannot be undone.")}
        dangerBtn
        submitBtnLabel={t("Delete")}
        submitting={deletePostMutation.isPending}
        onClose={() => setConfirmDeletePostId(null)}
        onSubmit={() => deletePostMutation.mutateAsync(confirmDeletePostId!)}
      />
      <ConfirmDialog
        visible={showDeleteMediaConfirm}
        title={t("Delete Media")}
        description={t("This media file will be permanently deleted. This action cannot be undone.")}
        dangerBtn
        submitBtnLabel={t("Delete")}
        submitting={deleteMediaMutation.isPending}
        onClose={() => setShowDeleteMediaConfirm(false)}
        onSubmit={() => deleteMediaMutation.mutateAsync()}
      />
    </>
  );
};

export default MediaDetailModal;
